import { Location } from '@angular/common';
import { Component, ViewChild, Inject, forwardRef, OnInit } from '@angular/core';

import { Router, ActivatedRoute } from '@angular/router';

import { UnitOfWork, StateMap, ErrorLogger, provideParent, UserManager } from '../../services/common';
import { EditManager, IEditHost, UtilFns } from '../../utils/common';
import { EditPropParent } from '../../controls/common';
import { Material, MaterialExposureCustomSurveyMeasure } from '../../entities/EntityModels';

import { MaterialSelectorComponent } from '../material/material-selector.component';
import { RecordExposureSurveyMeasuresComponent, MeasureEditData } from './record-exposure-survey-measures.component';

import { SelectableEntity } from '../../entities/projections/SelectableEntity';

import { ROUTES } from '../routes';
import * as _ from 'lodash';


@Component({
    selector: 'material-exposure-custom-survey-measures',
    templateUrl: './material-exposure-custom-survey-measures.html',
    providers: [provideParent(MaterialExposureCustomSurveyMeasuresComponent, EditPropParent)]
})
export class MaterialExposureCustomSurveyMeasuresComponent implements IEditHost<MaterialExposureCustomSurveyMeasure>, OnInit {

    @ViewChild(MaterialSelectorComponent, { static: true }) _materialSelectorComponent: MaterialSelectorComponent;

    public _isLoading = false;

    public _adding: boolean;
    public _unsurveyedMessage: string;
    public _validationMessage: string;

    // Default type ids
    public _typeCremeVersionId = 0;
    public _typeExposureMeasureId = 0;
    public _typeExposureMeasureValueId = 0;

    public _selectedMaterialExposureCustomSurveyMeasure: MaterialExposureCustomSurveyMeasure;
    public _materialExposureCustomSurveyMeasures: MaterialExposureCustomSurveyMeasure[] = [];

    public _selectedMaterial: Material;
    _edm: EditManager<MaterialExposureCustomSurveyMeasure>;
    public _materialExposureCustomSurveyMeasuresEx: SelectableEntity<MaterialExposureCustomSurveyMeasure>[] = [];

    private _data: MeasureEditData;

    constructor( @Inject(forwardRef(() => RecordExposureSurveyMeasuresComponent)) public _parent: RecordExposureSurveyMeasuresComponent,
                 private _uow: UnitOfWork, private _stateMap: StateMap, private _userManager: UserManager, private _router: Router,
                 public _route: ActivatedRoute, public _location: Location, public _errorLogger: ErrorLogger) {

        this._data = _parent._data;
    }

    ngOnInit() {
        this._edm = new EditManager<MaterialExposureCustomSurveyMeasure>(this, this._uow, this._stateMap, 'Custom Survey Material Measures');

        this._edm.pageState.canShowMessage = false;

        if (this._data.materialId > 0) {
            this.fetchMaterialAndCustomSurveys(this._data.materialId);
        }

        this.getDefaultMeasureTypeIds();
    }

    fetchMaterialAndCustomSurveys(materialId: number) {
        this._edm.entities = null;

        this._isLoading = true;
        this._uow.materialRepository.withId(materialId).then(m => {
            if (m) {
                this._selectedMaterial = m;
                this.fetchMeasures();
            }
            this._isLoading = false;
        });
    }

    fetchMeasures() {

        if (this._selectedMaterial == null) { return; }

        const params = { materialId: this._selectedMaterial.materialId };

        this._isLoading = true;
        this._uow.fetch('ExposureSurveys/MaterialExposureCustomSurveyMeasuresByMaterialId', params).then(m => {
            this._edm.entities = m;
            this._edm.updatePageState();

            this.formatSelectableEntities();

            this._isLoading = false;
        });
    }

    formatSelectableEntities() {
        this._materialExposureCustomSurveyMeasuresEx = [];
        if (this._edm.entities == null || this._edm.entities.length < 1) { return; }

        _.clone(this._edm.entities).forEach(element => {
            this._materialExposureCustomSurveyMeasuresEx.push(new SelectableEntity<MaterialExposureCustomSurveyMeasure>(element, '#FFFFFF', '', false));
        });
    }

    // Only show the message if there is a material selected and queried.
    hasNoCustomMeassureData(): boolean {
        if (this._selectedMaterial == null) {
            return false;
        }

        if (this._isLoading) {
            return false;
        }

        return (this._materialExposureCustomSurveyMeasuresEx == null || this._materialExposureCustomSurveyMeasuresEx.length < 1);
    }

    get isLoading() {
        return this._isLoading;
    }

    set isLoading(value) {
        this._isLoading = value;
    }

    isStaff() {
        return this._stateMap.currentUser.isStaff;
    }

    get utilFns() {
        return UtilFns;
    }

    canAdd() {
        return (this._selectedMaterial != null && !this._edm.editing);
    }

    // edit manager overrides
    canApply() {
        if (this._edm.editing) {
            return (this._uow.hasChanges() && !this._edm.currentEntity.entityAspect.hasValidationErrors);
        }

        return false;
    }

    canCancel() {
        return (this._uow.hasChanges());
    }

    canDeactivate() {
        return !this.hasEdits();
    }

    hasEdits() {
        return this._uow.hasChanges();
    }

    canSave() {
        if (this._edm.editing) { return false; }
        if (!this._edm.hasEdits()) { return false; }
        if (!this._edm.validateBeforeSave()) { return false; }
        return true;
    }

    canShowBack() {
        if (!this._edm.editing) { return false; }
        return !this._uow.hasChanges();
    }

    onAdd() {
        if (this._edm.entities == null) {
            this._edm.entities = [];
        }

        this._adding = true;
        const m = this._uow.materialExposureCustomSurveyMeasureFactory.create();

        m.customSurveyDate              = null;
        m.materialId                    = this._selectedMaterial.materialId;
        m.typeExposureMeasureId         = this._typeExposureMeasureId;
        m.typeExposureMeasureValueId    = this._typeExposureMeasureValueId;
        m.typeCremeVersionId 			= this._typeCremeVersionId;
        m.created                       = new Date(); // the date is displayed in the table after apply so a valid date is needed here.

        this._edm.entities.push(m);
        this._edm.onSelect(m);
    }

    onApply() {

        this._validationMessage = '';

        if (this._edm.currentEntity.customSurveyDate == null) {
            this._validationMessage = 'Please enter a valid Custom Survey Date.';
            return;

            const date = new Date(this._edm.currentEntity.customSurveyDate);

            if (isNaN(date.getTime())) {
                this._validationMessage = 'Please enter a valid Custom Survey Date.';
                return;
            }
        }

        if (this._edm.currentEntity.typeExposureMeasure == null) {
            this._validationMessage = 'Please select a Measure type other than None.';
            return;
        }

        if (this._edm.currentEntity.typeExposureMeasure.measureDescription.toLowerCase() == 'none') {
            this._validationMessage = 'Please select a Measure type other than None.';
            return;
        }

        if (this._edm.currentEntity.measureQualitativeResult == null && this._edm.currentEntity.measureQuantitativeResult == null) {
            this._validationMessage = 'Please enter a measure value.';
            return;
        }

        if (this._edm.currentEntity.typeCremeVersion.cremeVersion.toLowerCase() == 'none') {
            this._validationMessage = 'Please select a Creme Version other than None.';
            return;
        }

        this._edm.onApplyCore();
        this._adding = false;

        this.formatSelectableEntities();

        return true;
    }

    onBack() {
        this._adding = false;
        this._edm.editing = false;
    }

    onCancel() {
        this._validationMessage = '';

        this._adding = false;
        this._edm.onCancelCore();
        this._edm.entities = null;

        this._materialExposureCustomSurveyMeasuresEx = [];

        this.fetchMeasures();
    }

    onEdit(mx: SelectableEntity<MaterialExposureCustomSurveyMeasure>) {

        if (mx == null) { return; }

        this._selectedMaterialExposureCustomSurveyMeasure = this.filterToMaterialExposureCustomSurveyMeasureById(mx.data.materialExposureCustomSurveyMeasureId);

        if (this._selectedMaterialExposureCustomSurveyMeasure == null) { return; }

        this._edm.onSelect(this._selectedMaterialExposureCustomSurveyMeasure);
    }

    onRemove(mx: SelectableEntity<MaterialExposureCustomSurveyMeasure>) {

        if (mx == null) { return; }
        this._selectedMaterialExposureCustomSurveyMeasure = this.filterToMaterialExposureCustomSurveyMeasureById(mx.data.materialExposureCustomSurveyMeasureId);

        if (this._selectedMaterialExposureCustomSurveyMeasure == null) {
            this._validationMessage = 'Error occurred, could not remove the selected measure from the list.';
            return;
        }

        if (this._selectedMaterialExposureCustomSurveyMeasure.entityAspect.entityState.isAdded()) {
            this._selectedMaterialExposureCustomSurveyMeasure.entityAspect.rejectChanges();
        } else {
            this._selectedMaterialExposureCustomSurveyMeasure.entityAspect.setDeleted();
        }

        _.remove(this._edm.entities, this._selectedMaterialExposureCustomSurveyMeasure);

        this.formatSelectableEntities();
    }

    onSave() {
        if (this._edm.hasEdits()) {
            this._edm.onSaveCore().then(() => {

                this._adding = false;
                this._edm.editing = false;

                this.fetchMeasures();
            });
        }
    }

    onShowMaterial(mat: Material) {
        if (mat == null) { return; }

        this._router.navigate(UtilFns.asRouterParts(ROUTES.Material, mat.materialId, ROUTES.Material.childRoutes.MaterialExposure));
    }

    // Filters
    filterToMaterialExposureCustomSurveyMeasureById(materialExposureCustomSurveyMeasureId: number): MaterialExposureCustomSurveyMeasure {
        if (this._edm.entities == null || this._edm.entities.length < 1) {
            return null;
        }

        const measure = this._edm.entities.filter(m => m.materialExposureCustomSurveyMeasureId == materialExposureCustomSurveyMeasureId);
        if (measure != null && measure.length > 0) {
            return measure[0];
        }

        return null;
    }

    selectMaterial() {
        UtilFns.showModal(this._materialSelectorComponent, this).then(mli => {
            if (mli == null) { return; }
            return this._uow.materialRepository.withId(mli.materialId);
        }).then(m => {
            if (m) {
                this._selectedMaterial      = m;
                this._data.materialId       = m.materialId;

                this.fetchMeasures();
            }
        });
    }

    getDefaultMeasureTypeIds() {
        this._uow.typeExposureMeasureRepository.all().then(m => {
            if (m != null && m.length > 0) {
                const types = m.filter(t => t.measureName.toLowerCase() == 'none');
                if (types != null && types.length > 0) {
                    this._typeExposureMeasureId = types[0].typeExposureMeasureId;
                    this._typeExposureMeasureValueId = types[0].typeExposureMeasureValueId;
                }
            }
        });

        this._uow.typeCremeVersionRepository.all().then(c => {
            if (c != null && c.length > 0) {
                const types = c.filter(t => t.cremeVersion.toLowerCase() == 'none');
                if (types != null && types.length > 0) {
                    this._typeCremeVersionId = types[0].typeCremeVersionId;
                }
            }
        });
    }
}
